import { useViewer } from "@/authentication/ViewerProvider"
import Container from "@/components/ui/container"
import ErrorScreen from "@/components/ui/error-screen"
import { H1 } from "@/components/ui/headings"
import Panel from "@/components/ui/panel"
import { useToast } from "@/components/ui/use-toast"
import { gql } from "@/generated"
import { ContributorPermission } from "@/generated/graphql"
import { MainLayout } from "@/novus/layouts/MainLayout"
import LoadingScreen from "@/novus/components/loading-screen"
import { rootPath } from "@/novus/novus-paths"
import { useMutation, useQuery } from "@apollo/client"
import React from "react"
import { SubmitHandler } from "react-hook-form"
import { useNavigate, useParams } from "react-router-dom"
import invariant from "tiny-invariant"
import FiveTwoNineAccountForm, {
  FiveTwoNineAccountFormValues,
} from "../components/FiveTwoNineAccountForm"
import DeleteFiveTwoNineAccountButton from "./components/DeleteFiveTwoNineAccountButton"

const FiveTwoNineAccount_Query = gql(`
  query BeneficiaryFiveTwoNineAccount($idOrSlug: String) {
    fiveTwoNineAccount(idOrSlug: $idOrSlug) {
      ...ProfileFiveTwoNineAccountFragment
    }
  }
`)

const UpdateFiveTwoNineAccount_Mutation = gql(`
  mutation UpdateBeneificaryFiveTwoNineAccount($input: UpdateFiveTwoNineAccountInput!) {
    updateFiveTwoNineAccount(input: $input) {
      fiveTwoNineAccount {
        ...ProfileFiveTwoNineAccountFragment
      }
      errors {
        path
        fullMessages
      }
    }
  }
`)

const EditFiveTwoNineAccountScreen = () => {
  const { id } = useParams()
  invariant(id, "expected id")
  const { toast } = useToast()
  const [mutate] = useMutation(UpdateFiveTwoNineAccount_Mutation)
  const { data, loading, error } = useQuery(FiveTwoNineAccount_Query, {
    variables: { idOrSlug: id },
    skip: !id,
  })
  const navigate = useNavigate()
  const { viewer } = useViewer()

  if (loading) return <LoadingScreen />
  if (error) return <ErrorScreen message={error?.message} />

  const fiveTwoNineAccount = data?.fiveTwoNineAccount

  const defaultValues: FiveTwoNineAccountFormValues = {
    sponsorStateId: fiveTwoNineAccount?.program?.sponsorState?.id || "",
    programId: fiveTwoNineAccount?.program?.id || "",
    accountNumber: fiveTwoNineAccount?.accountNumber || "",
    beneficiaryFirstName: fiveTwoNineAccount?.beneficiaryFirstName || "",
    beneficiaryLastName: fiveTwoNineAccount?.beneficiaryLastName || "",
    beneficiaryBirthdate: fiveTwoNineAccount?.beneficiaryBirthdate || "",
  }

  const onSubmit: SubmitHandler<FiveTwoNineAccountFormValues> = async (
    values,
  ) => {
    const { data } = await mutate({
      variables: {
        input: {
          id,
          programId: values.programId,
          beneficiaryFirstName: values.beneficiaryFirstName,
          beneficiaryLastName: values.beneficiaryLastName,
          beneficiaryBirthdate: values.beneficiaryBirthdate || undefined,
          accountNumber: values.accountNumber,
          accountOwnerFirstName: viewer.firstName || "",
          accountOwnerLastName: viewer.lastName || "",
          contributorPermission: ContributorPermission.Friends,
          planId: null,
        },
      },
    })

    if (
      data?.updateFiveTwoNineAccount.errors &&
      data?.updateFiveTwoNineAccount.errors.length > 0
    ) {
      throw data?.updateFiveTwoNineAccount.errors
    }
    toast({
      title: "Beneficiary has been updated.",
    })

    navigate(rootPath.pattern)
  }

  return (
    <MainLayout>
      <Container>
        <Panel backPath={rootPath.pattern} minHeight={true}>
          <div className="text-center mb-14">
            <H1>Edit Beneficiary</H1>
          </div>
          <FiveTwoNineAccountForm
            onSubmit={onSubmit}
            defaultValues={defaultValues}
          />
        </Panel>
        <div className="flex items-center justify-center mt-8">
          <DeleteFiveTwoNineAccountButton id={id} />
        </div>
      </Container>
    </MainLayout>
  )
}

export default EditFiveTwoNineAccountScreen
